import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { JsonSettingsType } from './../enums/JsonSettingsType';
import { formatDateIso } from '@/utilities/dateutils';

export class JsonSettings extends BaseObj {
    public Data: string;
    public __data: string;

    public EventId: number | null;
    public __eventid: number | null;

    public Expires: Date | null;
    public __expires: Date | null;

    public Id: number;
    public __id: number;

    public OrganizerId: number | null;
    public __organizerid: number | null;

    public Type: JsonSettingsType;
    public __type: JsonSettingsType;

    public UserId: number | null;
    public __userid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Data?: string | null;
        EntityState?: EntityState;
        EventId?: number | null;
        Expires?: Date | null;
        Id?: number;
        OrganizerId?: number | null;
        Type?: JsonSettingsType;
        UserId?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<JsonSettings>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('JsonSettings tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.Data = values.Data ?? '';
        this.__data = values.Data ?? '';
        Object.defineProperty(this, 'Data', {
            get(): string {
                return this.__data;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Data', this.__data, value);
                this.__data = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventId = values.EventId ?? null;
        this.__eventid = values.EventId ?? null;
        Object.defineProperty(this, 'EventId', {
            get(): number | null {
                return this.__eventid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EventId', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Expires = values.Expires ?? null;
        this.__expires = values.Expires ?? null;
        Object.defineProperty(this, 'Expires', {
            get(): Date | null {
                return this.__expires;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Expires', this.__expires, value);
                this.__expires = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Id = values.Id ?? 0;
        this.__id = values.Id ?? 0;
        Object.defineProperty(this, 'Id', {
            get(): number {
                return this.__id;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Id', this.__id, value);
                this.__id = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OrganizerId = values.OrganizerId ?? null;
        this.__organizerid = values.OrganizerId ?? null;
        Object.defineProperty(this, 'OrganizerId', {
            get(): number | null {
                return this.__organizerid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('OrganizerId', this.__organizerid, value);
                this.__organizerid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Type = values.Type ?? 'Production';
        this.__type = values.Type ?? 'Production';
        Object.defineProperty(this, 'Type', {
            get(): JsonSettingsType {
                return this.__type;
            },
            set(value: JsonSettingsType) {
                this.__entityStateHandler.propertyChanged('Type', this.__type, value);
                this.__type = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UserId = values.UserId ?? null;
        this.__userid = values.UserId ?? null;
        Object.defineProperty(this, 'UserId', {
            get(): number | null {
                return this.__userid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('UserId', this.__userid, value);
                this.__userid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"Data": ${JSON.stringify(this.Data)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventId": ${JSON.stringify(this.EventId)}` +
            ',' +
            `"Expires": ${this.Expires ? '"' + formatDateIso(this.Expires) + '"' : null}` +
            ',' +
            `"Id": ${JSON.stringify(this.Id)}` +
            ',' +
            `"OrganizerId": ${JSON.stringify(this.OrganizerId)}` +
            ',' +
            `"Type": ${this.Type == 'Production' ? 0 : this.Type == 'ContestantImport' ? 1 : this.Type == 'TrackingController' ? 2 : this.Type == 'PinnedViews' ? 3 : this.Type == 'GridState' ? 4 : this.Type == 'DismissedMessages' ? 5 : this.Type == 'Matrix' ? 6 : this.Type == 'Sprint' ? 7 : this.Type == 'Speaker' ? 8 : this.Type == 'EventJsonSettings' ? 9 : 0}` +
            ',' +
            `"UserId": ${JSON.stringify(this.UserId)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of JsonSettings from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Data?: string | null;
        EventId?: number | null;
        Expires?: Date | null;
        Id?: number;
        OrganizerId?: number | null;
        Type?: JsonSettingsType;
        UserId?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): JsonSettings {
        return new JsonSettings(values);
    }

    public static _FromServerJson(json: any): JsonSettings {
        return new JsonSettings({
            Data: json.Data ?? '',
            EventId: json.EventId ?? null,
            Expires: json.Expires ? (json.Expires instanceof Date ? json.Expires : new Date(json.Expires)) : null,
            Id: json.Id,
            OrganizerId: json.OrganizerId ?? null,
            Type:
                json.Type == 0
                    ? 'Production'
                    : json.Type == 1
                      ? 'ContestantImport'
                      : json.Type == 2
                        ? 'TrackingController'
                        : json.Type == 3
                          ? 'PinnedViews'
                          : json.Type == 4
                            ? 'GridState'
                            : json.Type == 5
                              ? 'DismissedMessages'
                              : json.Type == 6
                                ? 'Matrix'
                                : json.Type == 7
                                  ? 'Sprint'
                                  : json.Type == 8
                                    ? 'Speaker'
                                    : json.Type == 9
                                      ? 'EventJsonSettings'
                                      : 'Production',
            UserId: json.UserId ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of JsonSettings from another instance of JsonSettings.
     * @param other The JsonSettings you want to clone
     * @returns A cloned JsonSettings
     */
    public static FromOther(other: JsonSettings): JsonSettings {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return JsonSettings._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return JsonSettings.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('Data' | 'EventId' | 'Expires' | 'Id' | 'OrganizerId' | 'Type' | 'UserId')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'JsonSettings';
}
